import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import identity from 'transmute/identity';
import { useReactNodeView } from '../utils/ReactNodeView';
import { useImageUploadHandler } from '../utils/useImageUploadHandler';
import { ImageFocusPlugin } from '../plugins/ImageFocusPlugin';
import { Tracker } from '../../utils/usageTracker';
import { INSERT_LINK } from 'rich-text-lib/constants/usageTracking';
import { PM_LINK } from '../constants/events';
import { ProsemirrorImageComponent } from '../../draft-transplant/ImageComponent';
import { jsx as _jsx } from "react/jsx-runtime";
export default function ImageView() {
  const context = useReactNodeView();
  const initialAttrs = useMemo(() => context.node ? context.node.attrs : {}, [context.node]);
  const attrs = useRef(initialAttrs);
  const [selected, setSelected] = useState(false);
  const view = context.view;
  const handleBlur = useCallback(() => {
    if (view) {
      view.dispatch(view.state.tr.setMeta(ImageFocusPlugin, {
        imagePos: null
      }));
    }
    setSelected(false);
    return false;
  }, [setSelected, view]);
  const handleDelete = useCallback(event => {
    if ((event.key === 'Backspace' || event.key === 'Delete') && selected) {
      const pos = typeof context.getPos === 'function' ? context.getPos() : undefined;
      if (view && pos) {
        let tr = view.state.tr;
        tr = tr.delete(pos, pos + 1);
        view.dispatch(tr);
        setSelected(false);
      }
    }
  }, [view, selected, context, setSelected]);
  useEffect(() => {
    window.addEventListener('keydown', handleDelete);
    return () => {
      window.removeEventListener('keydown', handleDelete);
    };
  }, [handleDelete]);
  const updateImage = useCallback(data => {
    const pos = typeof context.getPos === 'function' ? context.getPos() : undefined;
    if (view && pos) {
      const {
        image,
        link
      } = data;
      let tr = view.state.tr;
      const changes = image || {};
      if (link) {
        changes.link = link;
        Tracker.track(PM_LINK, {
          action: INSERT_LINK,
          noFollow: link.isNoFollow,
          externalLink: link.isTargetBlank,
          isImage: true
        });
      } else {
        if (image.width === attrs.current.width) {
          return;
        }
      }
      const newAttrs = Object.assign({}, attrs.current, changes);
      tr = tr.setNodeMarkup(pos, undefined, newAttrs);
      attrs.current = newAttrs;
      view.dispatch(tr);
    }
  }, [view, context]);
  useImageUploadHandler({
    updateImage,
    attrs
  });
  const onFocus = useCallback(() => {
    if (view) {
      if (view.props.handleClick) {
        view.props.handleClick(view, 0, new MouseEvent('click'));
      }
      const props = Object.assign({}, view.props, {
        handleClick: handleBlur
      });
      view.setProps(props);
      view.dom.blur();
      const imagePos = typeof context.getPos === 'function' && context.getPos();
      view.dispatch(view.state.tr.setMeta(ImageFocusPlugin, {
        imagePos
      }));
    }
    setSelected(true);
  }, [setSelected, view, handleBlur, context]);
  return /*#__PURE__*/_jsx(ProsemirrorImageComponent, {
    restoreKeyDown: identity,
    overrideKeyDown: identity,
    onFocus: onFocus,
    selected: selected,
    useProsemirror: true,
    prosemirrorProps: Object.assign({}, attrs.current, {
      updateImage
    })
  });
}
export const imageViewUpdateFn = ({
  previousNode,
  node
}) => {
  if (previousNode.attrs.src !== node.attrs.src || previousNode.type.name !== node.type.name || previousNode.attrs.align !== node.attrs.align) {
    return false;
  }
  return true;
};